import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Link } from "../models/Link";
import { LinkService } from "../services/LinkService";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface LinkContextProps {
  links: Link[];
  selectedLink: Link | null;
  setSelectedLink: (linkId: string) => void;
  createLink: (
    linkData: Omit<
      Link,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "updatedById"
      | "matterId"
    >,
  ) => Promise<string>;
  updateLink: (
    linkId: string,
    updatedData: Partial<Omit<Link, "_id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  deleteLinkById: (linkId: string) => Promise<void>;
  loading: Loadings;
  error: Errors;
}

const LinkContext = createContext<LinkContextProps | undefined>(undefined);

export const useLink = (): LinkContextProps => {
  const context = useContext(LinkContext);
  if (!context) {
    throw new Error("useLink must be used within a LinkProvider");
  }
  return context;
};

export const LinkProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();
  const [links, setLinks] = useState<Link[]>([]);
  const [selectedLink, setSelectedLink] = useState<Link | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: true,
    details: true,
  });

  useSocketEventByIds("links:changed", ["*"], () => fetchLinks());

  const fetchLinks = async () => {
    if (!user || !currentMatterId) return;
    setLoadings((prev) => ({ ...prev, list: true }));
    try {
      const fetchedLinks = await LinkService.getLinks(currentMatterId);
      setLinks(fetchedLinks);
      setLoadings((prev) => ({ ...prev, list: false }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, list: err }));
      setLoadings((prev) => ({ ...prev, list: false }));
    }
  };

  useEffect(() => {
    if (!user || !currentMatterId) return;
    fetchLinks();
  }, [user, currentMatterId]);

  const setSelectedLinkById = async (linkId: string) => {
    if (!currentMatterId) return;
    setLoadings((prev) => ({ ...prev, details: true }));
    try {
      const linkData = await LinkService.getLink(linkId);
      setSelectedLink(linkData);
      setLoadings((prev) => ({ ...prev, details: false }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, details: err }));
      setLoadings((prev) => ({ ...prev, details: false }));
    }
  };

  const createLink = async (
    linkData: Omit<
      Link,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "updatedById"
      | "matterId"
    >,
  ): Promise<string> => {
    const newLinkId = await LinkService.createLink({
      ...linkData,
      matterId: currentMatterId!,
    });
    await fetchLinks(); // Update list after creation
    return newLinkId;
  };

  const updateLink = async (
    linkId: string,
    updatedData: Partial<Omit<Link, "_id" | "createdAt" | "updatedAt">>,
  ) => {
    await LinkService.updateLink(linkId, updatedData);
    await setSelectedLinkById(linkId);
    await fetchLinks(); // Refresh list if needed
  };

  const deleteLinkById = async (linkId: string) => {
    if (!currentMatterId) return;
    await LinkService.deleteLink(linkId);
    setLinks((prev) => prev.filter((l) => l._id !== linkId));
    setSelectedLink(null);
  };

  return (
    <LinkContext.Provider
      value={{
        links,
        selectedLink,
        createLink,
        updateLink,
        deleteLinkById,
        setSelectedLink: setSelectedLinkById,
        loading: loadings,
        error: errors,
      }}
    >
      {children}
    </LinkContext.Provider>
  );
};
